/* ──────────────────────────────────────────────────────────────
   RUN24 — Search overlay + predictive search.

   Contrato: abre al recibir window event 'r24:open-search' (lo
   dispara el header). Cierra con Esc, scrim, botón "Cerrar · esc".

   Predictive search: fetch propio a routes.predictive_search_url
   con section rendering de la sección run24-search-overlay
   (?section_id=run24-search-overlay). El bloque #R24PredictiveResults
   de la respuesta trae productos ([data-products]) y sugerencias
   ([data-queries]) ya renderizados en Liquid. Mismo endpoint/patrón
   que predictive-search.js de Dawn, con markup 100% propio.
   ────────────────────────────────────────────────────────────── */

(() => {
  const DEBOUNCE_MS = 250;
  let debounceTimer = null;
  let abortController = null;
  let lastFocused = null;
  let isOpen = false;

  const el = (id) => document.getElementById(id);
  const root = () => el('R24Search');

  function open() {
    const r = root();
    if (!r || isOpen) return;
    isOpen = true;
    lastFocused = document.activeElement;
    r.hidden = false;
    document.body.classList.add('overflow-hidden');
    const input = el('R24SearchInput');
    if (input) {
      input.focus();
      input.select();
    }
  }

  function close() {
    const r = root();
    if (!r || !isOpen) return;
    isOpen = false;
    r.hidden = true;
    document.body.classList.remove('overflow-hidden');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function setDefaultState() {
    const results = el('R24SearchResults');
    const dflt = el('R24SearchDefault');
    const empty = el('R24SearchEmpty');
    const all = el('R24SearchAll');
    const tip = el('R24SearchTip');
    const quickTitle = el('R24SearchQuickTitle');
    const resultsTitle = el('R24SearchResultsTitle');
    const trending = el('R24SearchTrending');

    if (results) {
      results.hidden = true;
      results.innerHTML = '';
    }
    if (dflt) dflt.hidden = false;
    if (empty) empty.hidden = true;
    if (all) all.hidden = true;
    if (tip) tip.hidden = false;
    if (quickTitle) quickTitle.textContent = 'Buscado esta semana';
    if (resultsTitle) resultsTitle.textContent = 'Sugeridos · Staff';
    if (trending && trending.dataset.defaultHtml) trending.innerHTML = trending.dataset.defaultHtml;
  }

  function renderResults(doc, query) {
    const payload = doc.getElementById('R24PredictiveResults');
    const results = el('R24SearchResults');
    const dflt = el('R24SearchDefault');
    const empty = el('R24SearchEmpty');
    const all = el('R24SearchAll');
    const tip = el('R24SearchTip');
    const quickTitle = el('R24SearchQuickTitle');
    const resultsTitle = el('R24SearchResultsTitle');
    const trending = el('R24SearchTrending');
    if (!results) return;

    const products = payload ? payload.querySelector('[data-products]') : null;
    const queries = payload ? payload.querySelector('[data-queries]') : null;
    const count = products ? products.children.length : 0;

    if (dflt) dflt.hidden = true;
    if (tip) tip.hidden = true;
    results.innerHTML = products ? products.innerHTML : '';
    results.hidden = count === 0;
    if (empty) empty.hidden = count > 0;
    if (resultsTitle) {
      resultsTitle.textContent = count > 0 ? `${count} producto${count === 1 ? '' : 's'}` : 'Resultados';
    }

    if (all) {
      const r = root();
      const searchUrl = (r && r.dataset.searchUrl) || '/search';
      all.href = `${searchUrl}?q=${encodeURIComponent(query)}`;
      all.hidden = false;
    }

    if (trending) {
      if (!trending.dataset.defaultHtml) trending.dataset.defaultHtml = trending.innerHTML;
      if (queries && queries.children.length > 0) {
        if (quickTitle) quickTitle.textContent = 'Sugerencias';
        trending.innerHTML = queries.innerHTML;
      } else {
        if (quickTitle) quickTitle.textContent = 'Buscado esta semana';
        trending.innerHTML = trending.dataset.defaultHtml;
      }
    }
  }

  function search(query) {
    const r = root();
    if (!r) return;
    if (abortController) abortController.abort();

    const q = query.trim();
    if (q.length === 0) {
      setDefaultState();
      return;
    }

    abortController = new AbortController();
    const base = r.dataset.predictiveUrl || '/search/suggest';
    const params = new URLSearchParams({
      q: q,
      'resources[type]': 'product,query',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor',
      section_id: 'run24-search-overlay',
    });

    fetch(`${base}?${params.toString()}`, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        renderResults(doc, q);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        // Si el predictive search falla, deja que Enter lleve a /search?q=
        setDefaultState();
      });
  }

  function init() {
    const r = root();
    if (!r) return;
    const input = el('R24SearchInput');
    const clear = el('R24SearchClear');
    const trending = el('R24SearchTrending');
    if (trending) trending.dataset.defaultHtml = trending.innerHTML;

    window.addEventListener('r24:open-search', open);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        close();
      }
    });

    r.addEventListener('click', (event) => {
      if (event.target.closest('[data-r24-search-close]')) {
        close();
        return;
      }
      const term = event.target.closest('[data-r24-term]');
      if (term && input) {
        input.value = term.dataset.r24Term;
        input.focus();
        clear && (clear.hidden = false);
        search(input.value);
      }
    });

    if (input) {
      input.addEventListener('input', () => {
        if (clear) clear.hidden = input.value.length === 0;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => search(input.value), DEBOUNCE_MS);
      });
    }

    if (clear && input) {
      clear.addEventListener('click', () => {
        input.value = '';
        clear.hidden = true;
        input.focus();
        setDefaultState();
      });
    }

    // API pública mínima por si algún bloque quiere abrir la búsqueda
    window.R24Search = { open, close };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
